import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Account,
  ActivityFilterModel,
  ActivityOrderBy,
  cacheKeys,
  DayIntervalUnit,
  Issue,
  IssueMessageType,
  IssueType,
  Media,
  replaceMentionPlaceholderWithNames,
  Severity,
  useCreateMutation,
  useDeleteMutation,
  useFindByQuery,
  useTogglePin,
  useTranslatedText,
  useUpdateIssue,
  useUpdateMutation,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { navigate } from 'expo-router/build/global-state/routing';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import {
  colors,
  colors as CropCircleColor,
} from '../../components/crop-circle/tokens/colors';
import { Colors } from '../../themes';
import { getReminderDefaultValues } from '../../utils/reminderUtils';
import { ActivityCard } from '../crop-circle/Components/ActivityCard';
import { severityToVariant, TypeIcon } from '../crop-circle/Components/Type';
import { typeColors } from '../crop-circle/Components/Type/types';
import { Icon } from '../crop-circle/icons';
import { getIssueAuditData, getIssueFullName } from '../issue/issueFormatUtils';
import { IssueReminderModal } from '../issue/issueReminderModal';
import { LoadingIndicator } from '../loadingIndicator';
import { useTypedMediaItem } from '../media/useTypedMediaItem';
import { IssueCardOld } from './issueCard-old';

import SwipeableItemPackage from 'react-native-swipeable-item';

const SwipeableItem = Platform.OS !== 'web' ? SwipeableItemPackage : null;

export interface IssueCardData {
  type: string;
  asset: {
    name: string;
    coverImage?: {
      id?: string;
      url?: string;
      extension?: string;
      status?: string;
      type?: string;
    };
  };
  workspace: {
    id: string;
    name: string;
  };
  id: string;
  title: string;
  subtitle?: string;
  severity: Severity;
  createdAt: string;
  effectiveCreationDate?: string;
  isPinned?: boolean;
  updatedAt: string;
  labels?: {
    id: string;
    name: string;
  }[];
  nextReminderDate?: string;
  userMentions?: Account[];
  latestMessage: {
    text: string;
    createdBy: Account;
    userMentions: Account[];
    type: IssueMessageType;
  };
}

export type IssueCardProps = {
  account: Account | null;
  activity: IssueCardData;
  isCropCircleRedesignEnabled: boolean;
  showAssetName?: boolean;
  showImage?: boolean;
  isUnread?: boolean;
  isMentionedInInbox?: boolean;
  filter: ActivityFilterModel;
  borderRadius?: number;
  isSelected?: boolean;
  onRegisterSwipeable?: (id: string, ref: React.RefObject<any>) => void;
  onUnregisterSwipeable?: (id: string) => void;
  onSwipeOpen?: (id: string) => void;
  onSwipeClose?: (id: string) => void;
};

const SWIPEABLE_BUTTON_WIDTH = 150;

// Left side underlay - shows issue type (revealed when swiping LEFT)
// Renders two action buttons for red/green/yellow severities and a single
// gray navigator for blue/purple. Calls `updateIssue` on severity choices,
// or `onOpenIssue` when navigator is pressed.
const IssueTypeUnderlay = ({ item }: { item: IssueCardData }) => {
  const { t } = useTranslation();
  const { mutate: updateIssue } = useUpdateIssue();

  const severity = item?.severity;

  // Utility to perform update request and handle NOTE -> REPORT conversion
  const applySeverity = (newSeverity: Severity) => {
    const input: any = {
      id: item.id,
      severity: newSeverity,
    };

    if (
      item.type === 'NOTE' &&
      item.severity === 'purple' &&
      ['red', 'yellow', 'green'].includes(newSeverity)
    ) {
      input.type = 'REPORT';
    }

    updateIssue({ input });
  };

  const onOpenIssue = (issueId: string) => {
    navigate(`/activities/${issueId}`);
  };

  // Map severity to the two alternative severities to render when applicable
  const twoOptionsFor = {
    red: ['yellow', 'green'],
    green: ['yellow', 'red'],
    yellow: ['red', 'green'],
  } as Record<string, [Severity, Severity]>;

  // Blue or purple: show navigator
  if (severity === 'blue' || severity === 'purple') {
    return (
      <TouchableOpacity
        style={[
          styles.underlayRight,
          {
            width: SWIPEABLE_BUTTON_WIDTH,
            backgroundColor: colors.slate[400],
            alignItems: 'center',
            justifyContent: 'center',
          },
        ]}
        onPress={() => onOpenIssue(item.id)}
      >
        <Icon name={'Routine'} size={16} color="white" />
        <Text style={styles.underlayText}>{t('routines.header.view')}</Text>
      </TouchableOpacity>
    );
  }

  // For red/green/yellow show two buttons (right-most = edge-most)
  const opts = twoOptionsFor[severity] ?? ['green', 'red'];

  return (
    <View style={styles.underlayRight}>
      <TouchableOpacity
        style={[
          styles.underlayLeftSquare1,
          {
            left: SWIPEABLE_BUTTON_WIDTH,
            width: SWIPEABLE_BUTTON_WIDTH,
            backgroundColor: typeColors[severityToVariant[opts[0]] || 'done'],
          },
          styles.underlayActionButton,
        ]}
        onPress={() => applySeverity(opts[0])}
      >
        <View style={styles.underlayIconContainer}>
          <TypeIcon
            variant={severityToVariant[opts[0]] || 'done'}
            size={16}
            color="white"
          />
          <Text style={styles.underlayText}>
            {t(`issue.types.${severityToVariant[opts[0]]}`)}
          </Text>
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={[
          styles.underlayLeftSquare2,
          {
            width: SWIPEABLE_BUTTON_WIDTH,
            backgroundColor:
              typeColors[severityToVariant[opts[1]] || 'attention'],
          },
          styles.underlayActionButton,
        ]}
        onPress={() => applySeverity(opts[1])}
      >
        <View style={styles.underlayIconContainer}>
          <TypeIcon
            variant={severityToVariant[opts[1]] || 'attention'}
            size={16}
            color="white"
          />
          <Text style={styles.underlayText}>
            {t(`issue.types.${severityToVariant[opts[1]]}`)}
          </Text>
        </View>
      </TouchableOpacity>
    </View>
  );
};

// Right side underlay - reminder and pin/unpin action buttons
const ActionButtonsUnderlay = ({
  item,
  close,
}: {
  item: IssueCardData;
  close?: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { togglePin } = useTogglePin();
  const [isReminderModalVisible, setIsReminderModalVisible] = useState(false);

  // Check if reminder is available (no existing reminder AND issue type is not REMINDER)
  const isReminderAvailable = !!item?.nextReminderDate;

  const handleReminder = () => {
    setIsReminderModalVisible(true);
  };

  const handlePinUnpin = async () => {
    try {
      // Toggle pin state
      const newPinState = !item.isPinned;

      // Optimistically update the cache immediately
      queryClient.setQueryData(cacheKeys('issue').all, (oldData: any) => {
        if (!oldData) return oldData;

        // Update the specific issue in all cached queries
        const updateIssueInData = (data: any) => {
          if (data?.issue?.findIssues?.items) {
            return {
              ...data,
              issue: {
                ...data.issue,
                findIssues: {
                  ...data.issue.findIssues,
                  items: data.issue.findIssues.items.map((issue: any) =>
                    issue.id === item.id
                      ? { ...issue, isPinned: newPinState }
                      : issue
                  ),
                },
              },
            };
          }
          return data;
        };

        // Handle different cache structures
        if (Array.isArray(oldData)) {
          return oldData.map(updateIssueInData);
        }
        return updateIssueInData(oldData);
      });

      // Perform the mutation
      await togglePin(item.id, newPinState);

      // Invalidate and refetch to ensure consistency with server
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('issue').all,
        exact: false,
      });

      // Close the swipe after pin/unpin action completes
      close?.();
    } catch (error) {
      // If pin/unpin fails, revert optimistic update
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('issue').all,
        exact: false,
      });
      // Still close the swipe
      close?.();
    }
  };

  const onReminderSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: cacheKeys('workflow').all,
    });

    queryClient.invalidateQueries({
      queryKey: cacheKeys('issue').all,
      exact: false,
    });

    // Still close the swipe
    close?.();
  };

  // Convert item to Issue type for IssueReminderModal
  const issue: Issue = {
    id: item.id,
    workspace: item.workspace,
    type: item.type as IssueType,
    severity: item.severity,
    title: item.title,
    asset: item.asset,
  } as Issue;

  return (
    <>
      <View style={styles.underlayRight}>
        <TouchableOpacity
          style={[
            styles.underlayRightSquare1,
            {
              right: SWIPEABLE_BUTTON_WIDTH,
              width: SWIPEABLE_BUTTON_WIDTH,
            },
            styles.underlayActionButton,
          ]}
          onPress={handlePinUnpin}
        >
          <View style={styles.underlayIconContainer}>
            <MaterialCommunityIcons
              name={item.isPinned ? 'pin-off' : 'pin'}
              color="white"
              size={24}
            />
            <Text style={styles.underlayText}>
              {item.isPinned
                ? t('issue.menu.unpinToAsset')
                : t('issue.menu.pinToAsset')}
            </Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.underlayRightSquare2,
            {
              width: SWIPEABLE_BUTTON_WIDTH,
            },
            styles.underlayActionButton,
          ]}
          onPress={handleReminder}
        >
          <View style={styles.underlayIconContainer}>
            <MaterialCommunityIcons name="alarm" color="white" size={24} />
            <Text style={styles.underlayText}>{t('issue.menu.reminder')}</Text>
          </View>
        </TouchableOpacity>
      </View>
      {isReminderModalVisible && (
        <IssueReminderModalWrapper
          issue={issue}
          isReminderAvailable={isReminderAvailable}
          isReminderModalVisible={isReminderModalVisible}
          setIsReminderModalVisible={setIsReminderModalVisible}
          onReminderSuccess={onReminderSuccess}
          close={close}
        />
      )}
    </>
  );
};

const useIssueCardViewModel = (props: IssueCardProps) => {
  const { t } = useTranslation();
  const { activity } = props;
  const [auditData, setAuditData] = useState({ who: '...', when: '...' });
  const [isHovered, setIsHovered] = useState(false);

  const coverImageCandidate = activity.asset?.coverImage as unknown;
  const coverImageForTypedMedia = isMediaLike(coverImageCandidate)
    ? (coverImageCandidate as Media)
    : undefined;

  const { typedMediaItem: typedCoverImage } = useTypedMediaItem(
    coverImageForTypedMedia
  );

  useEffect(() => {
    setAuditData(
      getIssueAuditData(
        activity as unknown as Issue,
        props.filter.orderBy as ActivityOrderBy
      )
    );
  }, [activity, props.filter.orderBy]);

  const getProcessedTitle = () => {
    const titleText = activity.title;

    if (titleText.includes('@[') && (activity as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (activity as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }

    return titleText;
  };

  const getLatestMessageText = () => {
    const latestMessage = activity?.latestMessage;

    if (!latestMessage) {
      return '';
    }

    const user = getIssueFullName(
      latestMessage.createdBy?.id,
      latestMessage.createdBy?.name
    );

    if (props.isUnread && props.isMentionedInInbox) {
      return (
        <Text style={{ color: Colors.primary, fontSize: 14 }}>
          {t('activity.list.youAreMentioned')}
        </Text>
      );
    }

    if (
      latestMessage.type === IssueMessageType.RoutineMessage ||
      latestMessage.type === IssueMessageType.RoutineExceptionMessage
    ) {
      return activity?.subtitle
        ? `${user} ${activity.subtitle}`
        : t('routines.message.messageTitle', { name: user }).slice(0, -1);
    }

    if (
      latestMessage.type === IssueMessageType.SystemMessage ||
      latestMessage.type === IssueMessageType.RoutinePromptMessage
    ) {
      const { translatedText } = useTranslatedText('text', latestMessage);
      return translatedText;
    }

    if (!latestMessage.text) {
      return `${user}: ${t('issue.latestMessage.media')}`;
    }

    const { translatedText } = useTranslatedText('text', latestMessage);
    const messageText = translatedText || latestMessage?.text;

    const processedText = messageText.includes('@[')
      ? replaceMentionPlaceholderWithNames(
          messageText,
          latestMessage?.userMentions,
          t('mentions.unknownUser'),
          true
        )
      : messageText;

    return `${user}: ${processedText}`;
  };

  const latestMessageContent = getLatestMessageText();

  const renderLatestMessage = (textStyle: any, numberOfLines = 1) => {
    if (typeof latestMessageContent === 'string') {
      return (
        <Text
          variant="bodyMedium"
          style={textStyle}
          numberOfLines={numberOfLines}
        >
          {latestMessageContent}
        </Text>
      );
    }

    return latestMessageContent;
  };

  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {};

  return {
    activity,
    auditData,
    isHovered,
    hoverProps,
    typedCoverImage,
    getProcessedTitle,
    renderLatestMessage,
  };
};

function isMediaLike(value: unknown): value is Media {
  const v = value as any;
  return (
    !!v &&
    typeof v === 'object' &&
    typeof v.id === 'string' &&
    typeof v.extension === 'string' &&
    typeof v.type === 'string' &&
    typeof v.status === 'string'
  );
}

export const IssueCardBase = (props: IssueCardProps) => {
  const {
    activity,
    auditData,
    typedCoverImage,
    getProcessedTitle,
    renderLatestMessage,
  } = useIssueCardViewModel(props);

  const severityVariant = severityToVariant[activity.severity] || 'attention';

  const coverImage = typedCoverImage || activity.asset?.coverImage;
  const coverUri =
    coverImage?.url && `${coverImage.url}`.trim().length > 0
      ? coverImage.url
      : undefined;

  const showWorkspace = props.account && !props.account.workspace;

  return (
    <ActivityCard
      workspaceName={showWorkspace ? activity.workspace.name : undefined}
      assetName={activity.asset?.name || activity.workspace.name}
      showAssetName={props.showAssetName}
      imageUrl={coverUri}
      showImage={props.showImage}
      title={getProcessedTitle()}
      date={auditData.when}
      description={renderLatestMessage(issueCardDescriptionStyle, 1)}
      labels={activity.labels}
      severityVariant={severityVariant}
      hasReminder={!!activity.nextReminderDate}
      isPinned={activity.isPinned}
      isUnread={props.isUnread}
      isSelected={props.isSelected}
      borderRadius={props.borderRadius}
    />
  );
};

const issueCardDescriptionStyle = {
  fontSize: 14,
  fontWeight: '400' as const,
  color: '#6C6C6C',
  lineHeight: 20,
  flex: 1,
};

export const IssueCard = (props: IssueCardProps) => {
  // Hooks must be called unconditionally at the top
  const cardRef = useRef<View>(null);
  const swipeableRef = useRef<any>(null);
  const [isSwipeOpen, setIsSwipeOpen] = useState<boolean>(false);
  const swipeableId = props.activity?.id;

  useEffect(() => {
    if (!swipeableId) return;
    props.onRegisterSwipeable?.(swipeableId, swipeableRef);
    return () => {
      props.onUnregisterSwipeable?.(swipeableId);
    };
  }, [props, swipeableId]);

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleRedesignEnabled =
    props.isCropCircleRedesignEnabled || false;

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return <IssueCardOld {...props} />;
  }

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const cardContent = <IssueCardBase {...props} borderRadius={0} />;

  // Only enable swipeable on mobile platforms
  if (Platform.OS === 'web') {
    return <IssueCardBase {...props} />;
  }

  if (!SwipeableItem) {
    return <IssueCardBase {...props} />;
  }

  // Only allow left swipe for purple, red or yellow severity
  // Allow right-swipe behavior for common severities; red/green/yellow will
  // show two action buttons, whereas blue/purple will show a single gray
  // navigator button.
  const severity = props.activity?.severity;

  // Snap points: left underlay same as before; right snap depends on how many
  // buttons we render for the severity.
  const snapPointsLeft = [SWIPEABLE_BUTTON_WIDTH * 2];
  const snapPointsRight = ['red', 'green', 'yellow'].includes(severity || '')
    ? [SWIPEABLE_BUTTON_WIDTH * 2]
    : [SWIPEABLE_BUTTON_WIDTH];

  return (
    <SwipeableItem
      ref={swipeableRef}
      // track open state so we can decide whether to allow touches to continue
      onChange={(open: any) => {
        const isOpen =
          typeof open === 'boolean'
            ? open
            : typeof open?.snapPoint === 'number'
            ? open.snapPoint > 0
            : open?.openDirection && open.openDirection !== 'NONE';

        setIsSwipeOpen(isOpen);

        if (swipeableId) {
          if (isOpen) {
            props.onSwipeOpen?.(swipeableId);
          } else {
            props.onSwipeClose?.(swipeableId);
          }
        }
      }}
      item={props.activity}
      renderUnderlayLeft={(params: any) => {
        return (
          <ActionButtonsUnderlay item={props.activity} close={params.close} />
        );
      }}
      renderUnderlayRight={() => <IssueTypeUnderlay item={props.activity} />}
      snapPointsLeft={snapPointsLeft}
      snapPointsRight={snapPointsRight}
      overSwipe={0}
      key={props.activity.id}
    >
      <View
        ref={cardRef}
        style={{ borderRadius: 0, overflow: 'visible' }}
        onTouchStart={() => {
          if (swipeableId) {
            props.onSwipeOpen?.(swipeableId);
          }
        }}
        // If a swipe underlay is open, close it and consume the touch.
        // Only allow the touch to continue if the swipeable is currently closed.
        onStartShouldSetResponder={() => {
          if (isSwipeOpen) {
            swipeableRef.current?.close?.();
            setIsSwipeOpen(false);
            return true; // consume the touch so the content doesn't react
          }

          return false; // allow the touch to continue to children
        }}
      >
        {cardContent}
      </View>
    </SwipeableItem>
  );
};

const IssueReminderModalWrapper = ({
  issue,
  isReminderAvailable,
  isReminderModalVisible,
  setIsReminderModalVisible,
  onReminderSuccess,
  close,
}: {
  issue: any;
  isReminderAvailable: boolean;
  isReminderModalVisible: boolean;
  setIsReminderModalVisible: (visible: boolean) => void;
  onReminderSuccess: () => void;
  close?: () => void;
}) => {
  let workflowId: string | null = null;

  let defaultValues: any = {
    alertType: '',
    inputField: '',
    days: 1,
    dayInterval: 1,
    dayIntervalUnit: DayIntervalUnit.Days,
    daysStartDate: moment().format('MM/DD/YYYY'),
    onDate: undefined,
    notes: '',
  };

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  const { mutateAsync: updateWorkflow } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const { mutateAsync: deleteWorkflow } = useDeleteMutation({
    modelName: 'workflow',
    query: DELETE_WORKFLOW,
  });

  const submitReminder = async (input: any) => {
    if (isReminderAvailable) {
      await updateReminder(input);
    } else {
      await createReminder(input);
    }
  };

  const onSuccess = () => {
    setIsReminderModalVisible(false);
    onReminderSuccess();
  };

  const createReminder = async (input: any) => {
    const workflowInput: any = {
      ...input,
      workspace: {
        id: issue.workspace.id,
      },
    };

    await createWorkflow(
      { input: workflowInput },
      {
        onSuccess: async () => {
          onSuccess();
        },
      }
    );
  };

  const updateReminder = async (input: any) => {
    input = {
      ...input,
      id: workflowId,
    };

    await updateWorkflow(
      { input },
      {
        onSuccess: async () => {
          onSuccess();
        },
      }
    );
  };

  const deleteReminder = async () => {
    const input = {
      id: workflowId,
    };

    await deleteWorkflow(
      { input },
      {
        onSuccess: () => {
          onSuccess();
        },
      }
    );
  };

  const filter = useMemo(() => {
    return {
      trigger: {
        type: {
          eq: 'ISSUE',
        },
      },
      and: [
        {
          disabled: {
            ne: true,
          },
        },
      ],
      json: { 'trigger.config.issueId': issue.id },
    };
  }, [issue.id]);

  const { data: workflows, isLoading } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter },
    options: {
      enabled: isReminderModalVisible,
    },
  });

  if (isReminderAvailable && isReminderModalVisible) {
    if (isLoading) {
      return <LoadingIndicator />;
    }

    if (workflows?.items?.length > 0) {
      workflowId = workflows.items[0].id;
      defaultValues = getReminderDefaultValues(workflows.items[0]);
    }
  }

  return (
    <IssueReminderModal
      defaultValues={defaultValues}
      issue={issue}
      visible={isReminderModalVisible}
      hide={() => {
        setIsReminderModalVisible(false);
        close?.();
      }}
      isEditReminder={isReminderAvailable}
      submitReminder={submitReminder}
      deleteReminder={deleteReminder}
    />
  );
};

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
      }
    }
  }
`;

const CREATE_WORKFLOW = `mutation createWorkflow($input: CreateWorkflowInput!) {
  createWorkflow(input: $input) {
    id
  }
}`;

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
  updateWorkflow(input: $input) {
    id
  }
} 
`;

const DELETE_WORKFLOW = `
  mutation DeleteWorkflow($input: MutateWorkflowInput!) {
    deleteWorkflow(input: $input) {
      id
    }
  }
`;

const styles = StyleSheet.create({
  underlayRight: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  underlayRightSquare1: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    backgroundColor: CropCircleColor.orange[300],
  },
  underlayRightSquare2: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: '#2CA9E0', // cyan
  },
  underlayLeftSquare1: {
    position: 'absolute',
    top: 0,
    bottom: 0,
  },
  underlayLeftSquare2: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
  },
  underlayIconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  underlayText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
    marginTop: 8,
  },
  underlayActionButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
});
